import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Separator} from 'platform/components';
import {Center, Icon} from 'platform/foundation';
import styled, {css} from 'styled-components';

import {COMPARISON_HEADER_ATTRIBUTE} from '../constants/COMPARISON_HEADER_ATTRIBUTE';
import {useComparisonHover} from '../hooks/useComparisonHover';
import {ComparisonVehicleHeader} from './ComparisonVehicleHeader';
import {VehicleColumn, VehicleColumnProps} from './VehicleColumn';

const COMPARISON_COLUMN_HOVER_OPACITY = 0.06;
const DISABLED_DRAG_HANDLE_OPACITY = 0.5;
const INACTIVE_VEHICLE_GRAYSCALE = 1;
const INACTIVE_VEHICLE_BRIGHTNESS = 0.88;

type SortableVehicleColumnProps = VehicleColumnProps & {isDragDisabled?: boolean};

export function SortableVehicleColumn(props: SortableVehicleColumnProps) {
  const {hasUnifiedSurfaces} = useComparisonHover();
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: props.vehicle.sourcingVehicle.id,
    disabled: props.isDragDisabled,
  });
  const transformString = CSS.Transform.toString(transform);

  return (
    <>
      <SortableVehicleColumnWrapper
        ref={setNodeRef}
        $isDragging={isDragging}
        $transform={transformString}
        $transition={transition}
        $enhancedSurfaces={hasUnifiedSurfaces}
        $isInactive={props.isInactiveAd}
      >
        <div {...{[COMPARISON_HEADER_ATTRIBUTE]: true}}>
          <Center>
            <DragHandle {...attributes} {...listeners} $isDisabled={props.isDragDisabled ?? false}>
              <Icon size={4} value="action/drag_indicator" color="text.tertiary" />
            </DragHandle>
          </Center>
        </div>
        <VehicleColumn
          vehicle={props.vehicle}
          minMaxValues={props.minMaxValues}
          isInactiveAd={props.isInactiveAd}
          data-testid={props['data-testid']}
        />
      </SortableVehicleColumnWrapper>
      <Separator orientation="vertical" spacing={0} />
    </>
  );
}

const SortableVehicleColumnWrapper = styled.div<{
  $isDragging: boolean;
  $transform: string | undefined;
  $transition: string | undefined;
  $enhancedSurfaces: boolean;
  $isInactive: boolean;
}>`
  width: ${({theme}) => theme.getSize(40)};
  flex-shrink: 0;
  background-color: ${({theme, $isInactive}) =>
    $isInactive ? theme.colors.palettes.neutral['30']['100'] : theme.colors.general.white};
  transform: ${({$transform}) => $transform};
  transition: ${({$transition}) => $transition};
  opacity: ${({$isDragging}) => ($isDragging ? 0.5 : 1)};
  ${({$isInactive}) =>
    $isInactive &&
    css`
      filter: grayscale(${INACTIVE_VEHICLE_GRAYSCALE}) brightness(${INACTIVE_VEHICLE_BRIGHTNESS});
    `}
  z-index: ${({$isDragging}) => ($isDragging ? 1 : 0)};

  ${({$enhancedSurfaces, theme}) =>
    $enhancedSurfaces &&
    css`
      position: relative;
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        background-color: ${theme.colors.palettes.blue[60][100]};
        opacity: 0;
        box-shadow:
          inset 1px 0 ${theme.colors.general.accent},
          inset -1px 0 ${theme.colors.general.accent};
      }
      @media (hover: hover) {
        &:hover::after {
          opacity: ${COMPARISON_COLUMN_HOVER_OPACITY};
        }
      }
      &:focus-within::after {
        opacity: ${COMPARISON_COLUMN_HOVER_OPACITY};
      }

      @media ${theme.devices.notebook}, ${theme.devices.desktop} {
        ${ComparisonVehicleHeader}::after {
          content: '';
          position: absolute;
          inset: 0;
          pointer-events: none;
          background-color: ${theme.colors.palettes.blue[60][100]};
          opacity: 0;
        }
        @media (hover: hover) {
          &:hover ${ComparisonVehicleHeader}::after {
            opacity: ${COMPARISON_COLUMN_HOVER_OPACITY};
          }
        }
        &:focus-within ${ComparisonVehicleHeader}::after {
          opacity: ${COMPARISON_COLUMN_HOVER_OPACITY};
        }
      }
    `}
`;

const DragHandle = styled.div<{$isDisabled: boolean}>`
  cursor: ${({$isDisabled}) => ($isDisabled ? 'default' : 'grab')};
  opacity: ${({$isDisabled}) => ($isDisabled ? DISABLED_DRAG_HANDLE_OPACITY : 1)};
  padding: ${({theme}) => theme.getSize(1)};
  border-radius: ${({theme}) => theme.radii.xSmall};
  &:hover {
    background-color: ${({theme}) => theme.colors.palettes.neutral['10']['100']};
  }
  &:active {
    cursor: grabbing;
  }
`;
